<template>
  <fragment>
    <form-component>
      <b-row class="filters">
        <b-col sm="12" lg="2">
          <input-datepicker
            id="from"
            v-model="from"
            name="from"
            label="From"
            type="text"
          />
        </b-col>
        <b-col sm="12" lg="2">
          <input-datepicker
            id="through"
            v-model="through"
            name="through"
            label="Through"
            type="text"
          />
        </b-col>
        <b-col offset-lg="4" offset-sm="0" sm="12" lg="4">
          <input-text
            id="searchCriteria"
            v-model="searchCriteria"
            name="searchCriteria"
            label="Search"
          />
        </b-col>
      </b-row>
    </form-component>
    <b-table
      class="crisis-table text-center"
      :busy="loading"
      :fields="fields"
      :items="report"
      head-variant="dark"
      responsive
      show-empty
      empty-text="No Crisis Cases Found"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
      no-local-sorting
      small
      @sort-changed="sortingChanged"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <Loader />
        </div>
      </template>
      <template #cell(assignedInstitutionName)="row">
        <form-component>
          <campus-search-drop-down
            :value="{
              name: row.item.assignedInstitutionName,
              id: row.item.assignedInstitutionId
            }"
            :record="row"
            :saving="row.item.savingInstitution"
            @input="saveInstitution"
          />
        </form-component>
      </template>
      <template #cell(show_notes)="row">
        <crisis-cases-notes-buttons :row="row" @input="saveNotes" />
      </template>
      <template #row-details="row">
        <b-card class="notes-card">
          <form-component>
            <input-text-area
              id="notes"
              v-model="row.item.notes.note"
              name="notes"
              label="notes"
              type="text"
              :disabled="!row.item.notes.edit"
              @input="() => { row.item.notes.updated = true }"
            />
          </form-component>
        </b-card>
      </template>
      <template #custom-foot>
        <crisis-cases-table-footer
          :report="report"
          :page-token="pageToken"
          @input="loadCrisisReport"
        />
      </template>
    </b-table>
  </fragment>
</template>

<script>

import CrisisClient from '@/api/ClinicalOps/CrisisClient';
import InputDatepicker from '@/components/inputs/InputDatepicker.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputTextArea from '@/components/inputs/InputTextArea.vue';
import moment from 'moment';
import FormComponent from '@/components/FormComponent.vue';
import Loader from '@/components/Loader.vue';
import CrisisCasesTableFooter from './CrisisCasesTableFooter.vue';
import CrisisCasesNotesButtons from './CrisisCasesNotesButtons.vue';
import CampusSearchDropDown from './CampusSearchDropDown.vue';

export default {
    name: 'CrisisCasesTable',
    components: {
        InputDatepicker,
        InputText,
        InputTextArea,
        FormComponent,
        Loader,
        CampusSearchDropDown,
        CrisisCasesTableFooter,
        CrisisCasesNotesButtons
    },
    data() {
      return {
        crisisClient: new CrisisClient(this.$http),
        report: [],
        searchCriteria: '',
        from: '',
        through: '',
        pageToken: 'ByCreatedDescendingCrisisCasesReportPageToken',
        loading: false,
        sortBy: 'ByCreated',
        sortDesc: true,
        sortDirection: 'desc',
        fields: [
                { key: 'externalCaseId', label: 'Case Number' },
                { key: 'created', label: 'Date / Time', sortable: true, sortKey: 'ByCreated' },
                { key: 'assignedInstitutionName', label: 'Campus', sortable: true, sortKey: 'ByInstitutionName' },
                { key: 'externalType', label: 'Case Type', sortable: true, sortKey: 'ByExternalType' },
                { key: 'externalParticipantId', label: 'Participant Id', },
                { key: 'issues', label: 'Presenting Issue', },
                { key: 'show_notes', label: 'Notes' }
            ],
      }
    },
    watch: {
      searchCriteria() {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(
          () => {
            this.pageToken = 'ByCreatedDescendingCrisisCasesReportPageToken';
            this.report.length = 0;
            this.loading = true;
            this.loadCrisisReport()
          },
          333
        );
      },
      from() {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        this.pageToken = 'ByCreatedDescendingCrisisCasesReportPageToken';
        this.report.length = 0;
        this.loading = true;

        this.loadCrisisReport();
      },
      through() {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        this.pageToken = 'ByCreatedDescendingCrisisCasesReportPageToken';
        this.report.length = 0;
        this.loading = true;

        this.loadCrisisReport();
      }
    },
    mounted() {
      this.loadCrisisReport();
    },
    methods: {
      async loadCrisisReport() {
        this.loading = true;

        try {
          const response = await this.crisisClient.getCrisisCasesReport(
            this.pageToken,
            this.from ? moment(this.from).startOf('day').toISOString() : undefined,
            this.through ? moment(this.through).endOf('day').toISOString() : undefined,
            this.searchCriteria ? this.searchCriteria.split(/\s+/).filter(Boolean) : []
          );

          this.pageToken = response.nextPageToken;
          const crisisCases = response.crisisCases.map(c => ({
              ...c,
              issues: c.issues.join(', '),
              created: moment(c.created).format('M/DD/yyyy hh:mm A'),
              notes: {
                note: c.internalExplainer,
                initialNote: c.internalExplainer,
                edit: false,
                updated: false,
                saving: false
              },
              savingInstitution: false,
            }))

          this.report = [...this.report, ...crisisCases];
        } catch (e) {
          this.$store.commit('setGlobalAlert', {
            msg: 'Failed to load records. Please try again!', error: true,
        });
        } finally {
          this.loading = false;
        }
      },
      async saveNotes(item) {
        try {
            await this.crisisClient.setCrisisCaseNotes(item.id, item.notes.note, undefined);
            item.notes.initialNote = item.notes.note;
        } catch (e) {
            this.$store.commit('setGlobalAlert', {
                msg: 'Failed to save note. Please try again!', error: true,
            });
        } finally {
            item.notes.edit = false;
            item.notes.updated = false;
            item.notes.saving = false;
        }
      },
      async saveInstitution(institution, row) {
        row.item.savingInstitution = true;
        try {
            await this.crisisClient.setCrisisCaseInstitution(row.item.id, institution.id);
            row.item.assignedInstitutionId = institution.id;
            row.item.assignedInstitutionName = institution.name;
        } catch (e) {
            this.$store.commit('setGlobalAlert', {
                msg: 'Failed to update campus name. Please try again!', error: true,
            });
        } finally {
            row.item.savingInstitution = false;
        }
      },
      sortingChanged(ctx) {
        if (ctx.sortBy) {
          const direction = ctx.sortDesc ? 'Descending' : 'Ascending';
          this.pageToken = `${ctx.sortBy + direction}CrisisCasesReportPageToken`;
          this.report.length = 0;
          this.loadCrisisReport();
        }
      },
    }
}
</script>

<style scoped lang="scss">

.filters {
  background-color: $dark-blue;
  margin-bottom: 10px;
  margin-right: 0px;
  margin-left: 0px;
  padding-top: 5px;
  @include border-radius($border-radius);
}

.crisis-table ::v-deep {

    @include border-radius($border-radius);
    margin-bottom: 0;


    .thead-dark th {
      background-color: $dark-blue !important;
      border: 0px;
    }

    td {
        background-color: $off-blue;
        vertical-align: middle;
        color: white;
    }

    .notes-card {
        min-height: 0;
        border: 0px;
        .card-body {
            padding-top: 0;
            padding-bottom: 0;
            background-color: $off-blue !important;
            textarea:disabled {
                background-color: $off-blue !important;
                border: none;
                border-radius:0;
                border-top: 1px solid $border-color;
            }
        }
    }
}
</style>
